import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { deals, stores } from '../data/mockData';
import { ShoppingCart, Ticket, Copy, ExternalLink, Heart, Flame, TrendingUp, Clock, ChevronDown, Filter } from 'lucide-react';
import { toast } from 'sonner';
import DealDialog from './DealDialog';
import { useAuth } from '../context/AuthContext';

const DealsSection = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [revealedCoupons, setRevealedCoupons] = useState({});
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingFavorite, setLoadingFavorite] = useState(null);
  
  const { isAuthenticated, isFavorite, addFavorite, removeFavorite } = useAuth();

  const handleToggleFavorite = async (e, dealId) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Please sign in to save favorites');
      return;
    }
    
    setLoadingFavorite(dealId);
    try {
      if (isFavorite(dealId)) {
        await removeFavorite(dealId);
        toast.success('Removed from favorites');
      } else {
        await addFavorite(dealId);
        toast.success('Added to favorites!');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingFavorite(null);
    }
  };

  const tabs = [
    { id: 'all', label: 'All Deals', icon: null },
    { id: 'popular', label: 'Popular', icon: TrendingUp },
    { id: 'hottest', label: 'Hottest', icon: Flame },
    { id: 'expiring', label: 'Expiring', icon: Clock },
  ];

  const prioritizedDeals = useMemo(() => {
    const fashionDeals = deals.filter(d => d.category === 'fashion');
    const cosmeticsDeals = deals.filter(d => d.category === 'cosmetics');
    const otherDeals = deals.filter(d => d.category !== 'fashion' && d.category !== 'cosmetics');
    
    const mixed = [];
    const maxLen = Math.max(fashionDeals.length, cosmeticsDeals.length, otherDeals.length);
    
    for (let i = 0; i < maxLen; i++) {
      if (fashionDeals[i]) mixed.push(fashionDeals[i]);
      if (cosmeticsDeals[i]) mixed.push(cosmeticsDeals[i]);
      if (otherDeals[i]) mixed.push(otherDeals[i]);
      if (otherDeals[i + maxLen]) mixed.push(otherDeals[i + maxLen]);
    }
    
    return mixed;
  }, []);

  const filteredDeals = prioritizedDeals.filter((deal) => {
    let tabMatch = true;
    if (activeTab === 'popular') tabMatch = deal.popular;
    else if (activeTab === 'hottest') tabMatch = deal.hottest;
    else if (activeTab === 'expiring') tabMatch = deal.expiringSoon;

    let categoryMatch = true;
    if (selectedCategory !== 'all') categoryMatch = deal.store === selectedCategory;

    return tabMatch && categoryMatch;
  }).slice(0, 15);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  };

  const handleBuyNow = (deal) => {
    const store = stores.find(s => s.slug === deal.store);
    setSelectedDeal({ ...deal, storeName: store?.name });
    setDialogOpen(true);
  };

  const handleRevealCoupon = async (deal) => {
    setRevealedCoupons(prev => ({
      ...prev,
      [deal.id]: true
    }));

    const copied = await copyToClipboard(deal.couponCode);
    toast.success(
      <div className="flex flex-col">
        <span className="font-semibold flex items-center gap-2">
          <Copy size={16} /> Coupon Code {copied ? 'Copied!' : 'Revealed!'}
        </span>
        <span className="text-lg font-mono bg-slate-100 text-slate-900 px-3 py-1 rounded-lg mt-2">{deal.couponCode}</span>
        <span className="text-sm text-slate-500 mt-1">Use at {deal.store.charAt(0).toUpperCase() + deal.store.slice(1)}</span>
      </div>,
      { duration: 5000 }
    );
  };

  const handleGoToStore = (deal) => {
    const store = stores.find(s => s.slug === deal.store);
    setSelectedDeal({ ...deal, storeName: store?.name });
    setDialogOpen(true);
  };

  const getStoreName = (storeSlug) => {
    const store = stores.find(s => s.slug === storeSlug);
    return store?.name || storeSlug;
  };

  const getBadgeColor = (badge) => {
    const colors = {
      'Mega Deal': 'bg-gradient-to-r from-orange-500 to-amber-500',
      'Hot Deal': 'bg-gradient-to-r from-red-500 to-pink-500',
      'Best price': 'bg-gradient-to-r from-slate-900 to-cyan-500',
      'Clearance': 'bg-gradient-to-r from-purple-500 to-slate-900',
      'Trending': 'bg-gradient-to-r from-pink-500 to-rose-500',
      'Limited Stock': 'bg-gradient-to-r from-amber-500 to-yellow-500',
      'Best seller': 'bg-gradient-to-r from-emerald-500 to-teal-500',
      'Best Value': 'bg-gradient-to-r from-slate-900 to-purple-500',
      'Fan Favorite': 'bg-gradient-to-r from-rose-500 to-red-500',
      'Luxury': 'bg-gradient-to-r from-amber-600 to-yellow-500',
      'Premium': 'bg-gradient-to-r from-slate-900 to-slate-700',
    };
    return colors[badge] || 'bg-gradient-to-r from-slate-900 to-slate-700';
  };

  const renderButton = (deal) => {
    const isRevealed = revealedCoupons[deal.id];

    switch (deal.buttonType) {
      case 'buy':
        return (
          <button 
            onClick={() => handleBuyNow(deal)}
            className="w-full bg-gradient-to-r from-slate-900 to-slate-700 text-white py-3 rounded-xl font-bold text-sm hover:shadow-lg hover:scale-[1.02] transition-all flex items-center justify-center gap-2 btn-press"
          >
            <ShoppingCart size={16} />
            Buy Now
          </button>
        );
      case 'bestDeal':
        return (
          <button 
            onClick={() => handleBuyNow(deal)}
            className="w-full bg-amber-400 text-slate-900 py-3 rounded-xl font-bold text-sm hover:bg-amber-300 transition-all btn-press"
          >
            Best Deal Today
          </button>
        );
      case 'coupon':
        if (isRevealed) {
          return (
            <div className="space-y-2">
              <div className="w-full bg-slate-100 border-2 border-dashed border-slate-700 text-slate-900 py-2 rounded-xl font-mono text-center font-bold text-sm">
                {deal.couponCode}
              </div>
              <button 
                onClick={() => handleGoToStore(deal)}
                className="w-full bg-gradient-to-r from-slate-900 to-slate-700 text-white py-2 rounded-xl font-semibold text-xs hover:shadow-lg transition-all flex items-center justify-center gap-1"
              >
                <ExternalLink size={14} />
                Go to Store
              </button>
            </div>
          );
        }
        return (
          <button 
            onClick={() => handleRevealCoupon(deal)}
            className="w-full bg-slate-100 border-2 border-dashed border-slate-300 text-slate-900 py-3 rounded-xl font-bold text-sm hover:bg-[#edd1d9] hover:border-slate-700 transition-all flex items-center justify-center gap-2 btn-press"
          >
            <Ticket size={16} />
            Reveal Coupon
          </button>
        );
      default:
        return (
          <button 
            onClick={() => handleBuyNow(deal)}
            className="w-full bg-gradient-to-r from-slate-900 to-slate-700 text-white py-3 rounded-xl font-bold text-sm hover:shadow-lg hover:scale-[1.02] transition-all btn-press"
          >
            Get Deal
          </button>
        );
    }
  };

  return (
    <section className="py-12 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-2">Top Bridal Deals</h2>
            <p className="text-slate-500">Handpicked savings just for you</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Tabs */}
            <div className="flex bg-white rounded-full p-1.5 shadow-sm border border-slate-200">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      activeTab === tab.id
                        ? 'bg-slate-900 text-white shadow-md'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    {IconComponent && <IconComponent size={14} />}
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Category Dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 px-4 py-2.5 rounded-full hover:border-slate-300 transition-colors shadow-sm"
              >
                <Filter size={16} />
                <span className="hidden sm:inline">Filter</span>
                <ChevronDown size={16} className={`transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl py-2 w-56 z-20 max-h-72 overflow-y-auto animate-fadeIn">
                  <button
                    onClick={() => {
                      setSelectedCategory('all');
                      setDropdownOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      selectedCategory === 'all' ? 'text-slate-900 bg-slate-100 font-medium' : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    All Stores
                  </button>
                  {stores.map((store) => (
                    <button
                      key={store.id}
                      onClick={() => {
                        setSelectedCategory(store.slug);
                        setDropdownOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        selectedCategory === store.slug ? 'text-slate-900 bg-slate-100 font-medium' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {store.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Deals Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
          {filteredDeals.map((deal, index) => (
            <div
              key={deal.id}
              className="bg-white rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300 group border border-slate-100 hover:border-slate-400 animate-fadeInUp opacity-0"
              style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}
            >
              {/* Image Container */}
              <div className="relative aspect-square bg-slate-50 p-4 overflow-hidden">
                {/* Badge */}
                {deal.badge && (
                  <div className="absolute top-3 left-3 z-10">
                    <div className={`text-white text-[10px] font-bold px-2.5 py-1 rounded-full ${getBadgeColor(deal.badge)} shadow-lg`}>
                      {deal.badge}
                    </div>
                  </div>
                )}

                {/* Favorite Button */}
                <button
                  onClick={(e) => handleToggleFavorite(e, deal.id)}
                  disabled={loadingFavorite === deal.id}
                  className={`absolute top-3 right-12 z-20 w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-md ${
                    isFavorite(deal.id) 
                      ? 'bg-red-500 text-white scale-110' 
                      : 'bg-white text-slate-400 hover:text-red-500 hover:scale-110'
                  }`}
                  data-testid={`favorite-btn-${deal.id}`}
                >
                  {loadingFavorite === deal.id ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Heart size={16} fill={isFavorite(deal.id) ? 'currentColor' : 'none'} />
                  )}
                </button>

                {/* Discount Badge */}
                <div className="absolute top-3 right-3 bg-amber-400 text-slate-900 text-xs font-black px-2.5 py-1 rounded-full z-10 shadow-md">
                  -{deal.discount}%
                </div>

                {/* Product Image */}
                <img
                  src={deal.image}
                  alt={deal.title}
                  className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=300&fit=crop';
                  }}
                />
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="text-sm font-semibold text-slate-800 line-clamp-2 mb-2 min-h-[40px] group-hover:text-slate-900 transition-colors">
                  {deal.title}
                </h3>
                
                {/* Price */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl font-black text-slate-900">${deal.salePrice?.toFixed(2)}</span>
                  <span className="text-sm text-slate-400 line-through">${deal.originalPrice?.toFixed(2)}</span>
                </div>

                <p className="text-slate-900 text-xs font-medium mb-3 line-clamp-1">{deal.cashback}</p>
                
                {renderButton(deal)}

                <Link
                  to={`/store/${deal.store}`}
                  className="block text-center text-slate-900 text-xs font-medium mt-3 hover:text-slate-700 transition-colors"
                >
                  See All {getStoreName(deal.store)} offers →
                </Link>
              </div>
            </div>
          ))}
        </div>

        {filteredDeals.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Filter size={32} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-lg">No deals found for the selected filters.</p>
            <button 
              onClick={() => { setActiveTab('all'); setSelectedCategory('all'); }}
              className="mt-4 text-slate-900 font-medium hover:text-slate-700"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      <DealDialog
        deal={selectedDeal}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        storeName={selectedDeal?.storeName}
      />
    </section>
  );
};

export default DealsSection;
